const cache = new Map();

export const GPS_PAIR_RE =
  /GPS:\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/g;

export function gpsCacheKey(latitude, longitude) {
  return `${Number(latitude).toFixed(4)},${Number(longitude).toFixed(4)}`;
}

export function uniqueNonEmpty(parts) {
  const seen = new Set();
  const out = [];
  for (const raw of parts) {
    const value = String(raw || "").trim();
    if (!value) continue;
    const key = value.replace(/^محافظة\s+/u, "").toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }
  return out;
}

export function formatPlaceFromBigDataCloud(payload = {}) {
  const parts = uniqueNonEmpty([
    payload.locality,
    payload.city,
    payload.principalSubdivision,
  ]);
  if (parts.length >= 1) return parts.slice(0, 3).join("، ");
  return String(payload.countryName || "").trim() || null;
}

export function extractGpsSegments(location) {
  const text = String(location || "").trim();
  if (!text) return [];
  return text.split("|").map((chunk) => {
    const label = chunk.trim();
    GPS_PAIR_RE.lastIndex = 0;
    const match = GPS_PAIR_RE.exec(label);
    const latitude = match ? Number(match[1]) : NaN;
    const longitude = match ? Number(match[2]) : NaN;
    return {
      label,
      latitude: Number.isFinite(latitude) ? latitude : null,
      longitude: Number.isFinite(longitude) ? longitude : null,
    };
  });
}

export function hideRawGpsInLabel(label) {
  const stripped = String(label || "")
    .replace(/\s*[—\-]\s*GPS:\s*-?\d+(?:\.\d+)?\s*,\s*-?\d+(?:\.\d+)?/gi, "")
    .replace(/\s*\(\s*GPS:\s*-?\d+(?:\.\d+)?\s*,\s*-?\d+(?:\.\d+)?\s*\)/gi, "")
    .trim();
  return stripped || String(label || "").trim();
}

export function mapsUrl(latitude, longitude) {
  if (!Number.isFinite(Number(latitude)) || !Number.isFinite(Number(longitude))) {
    return null;
  }
  return `https://www.google.com/maps?q=${Number(latitude)},${Number(longitude)}`;
}

async function fetchBigDataCloud(latitude, longitude) {
  const url =
    `https://api.bigdatacloud.net/data/reverse-geocode-client` +
    `?latitude=${encodeURIComponent(String(latitude))}` +
    `&longitude=${encodeURIComponent(String(longitude))}` +
    `&localityLanguage=ar`;
  const response = await fetch(url);
  if (!response.ok) return null;
  return formatPlaceFromBigDataCloud(await response.json());
}

export async function resolveGpsPlaceLabel(latitude, longitude) {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  const key = gpsCacheKey(lat, lng);
  if (cache.has(key)) return cache.get(key);

  let place = null;
  try {
    place = await fetchBigDataCloud(lat, lng);
  } catch {
    place = null;
  }
  cache.set(key, place);
  return place;
}

function replaceGpsInLabel(label, place) {
  if (!place) return label;
  return String(label).replace(
    /GPS:\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/g,
    place,
  );
}

export async function resolvePunchLocationText(location) {
  const text = String(location || "").trim();
  if (!text || !/GPS:/i.test(text)) return text;

  const segments = extractGpsSegments(text);
  const resolved = await Promise.all(
    segments.map(async (segment) => {
      if (segment.latitude == null || segment.longitude == null) {
        return segment.label;
      }
      if (!/^((حضور|انصراف)\s*:\s*)?GPS:/i.test(segment.label.trim())) {
        return segment.label;
      }
      const place = await resolveGpsPlaceLabel(
        segment.latitude,
        segment.longitude,
      );
      return replaceGpsInLabel(segment.label, place);
    }),
  );
  return resolved.join(" | ");
}
